import {
  useSingleUser,
  useSingleUserExtend,
} from "../../../hooks/useAdminUser";

import {
  GenreTraductor,
  translateUserStatus,
  User,
  UserExtend,
  userRolesTraductor,
  UserStatus,
} from "../../../types/user.types";
import StatusBox, {
  ColorType,
} from "../../../../../common/components/atom/StatusBox/StatusBox";
import { dateConverter } from "../../../../../utils/dateConverter";
import useNavigationApp from "../../../../../navigation/useNavigationApp";
import { limitString } from "../../../../../utils/string.utils";
import SingleOrganization from "../../../../organization/components/SingleOrganization";

export function UserFirstName({ userId }: { userId?: string }) {
  const {
    singleUserQuery: { isLoading, isError, data },
  } = useSingleUser(userId);

  if (isLoading) return <>...</>;
  if (isError) return <>-</>;

  return <>{data?.user_firstname}</>;
}

export function UserLastName({ userId }: { userId?: string }) {
  const {
    singleUserQuery: { isLoading, isError, data },
  } = useSingleUser(userId);

  if (isLoading) return <>...</>;
  if (isError) return <>-</>;

  return <>{data?.user_lastname}</>;
}

export function UserEmail({ userId }: { userId?: string }) {
  const {
    singleUserQuery: { isLoading, isError, data },
  } = useSingleUser(userId);

  if (isLoading) return <>...</>;
  if (isError) return <>-</>;

  return <>{data?.user_email}</>;
}

export interface UserInfoProps {
  userId?: string;
  info: keyof User;
  emptyText?: string;
  styled?: boolean;
  renderComponent?: (config: {
    labelRender: React.ReactNode;
    goTo: () => void;
  }) => JSX.Element;
  limitText?: number;
}

export function UserInfo({
  userId,
  info,
  emptyText,
  styled,
  renderComponent,
  limitText,
}: UserInfoProps) {
  const {
    singleUserQuery: { isLoading, isError, data },
  } = useSingleUser(userId);

  if (isLoading) return <>...</>;
  if (isError) return <>{emptyText ? emptyText : ""}</>;

  const translateInfo = (valueKey: keyof User, valueRaw: any) => {
    if (!valueKey) return "";
    switch (info) {
      case "user_gender":
        return GenreTraductor(valueRaw as any);
      case "user_last_connection":
        if (valueRaw === "0001-01-01T00:00:00Z") return "Jamais connecté·e";
        return dateConverter(valueRaw as any, { withTime: true });
      case "user_created_at":
        return dateConverter(valueRaw as any);
      case "user_updated_at":
        return dateConverter(valueRaw as any);
      default:
        return valueRaw;
    }
  };

  const value = translateInfo(info, data?.[info] || "");

  return <>{value ? limitString(value, limitText) : emptyText}</>;
}

export const UserOrganizationName = ({ userId }: { userId?: string }) => {
  const {
    singleUserQuery: { isLoading, isError },
  } = useSingleUser(userId);

  if (isLoading) return <>...</>;
  if (isError) return <>-</>;

  return <>No organization</>;
};

export interface UserExtendInfoProps {
  userOrgaId?: string;
  info: keyof UserExtend;
  emptyText?: string;
  styled?: boolean;
  renderComponent?: (config: {
    labelRender: React.ReactNode;
    goTo: () => void;
  }) => JSX.Element;
  limitText?: number;
}

export function UserExtendInfo({
  userOrgaId,
  info,
  emptyText,
  styled,
  renderComponent,
  limitText,
}: UserExtendInfoProps) {
  const {
    singleUserExtendQuery: { isLoading, isError, data },
  } = useSingleUserExtend(userOrgaId);
  const navigationApp = useNavigationApp();

  if (isLoading) return <>...</>;
  if (isError) return <>{emptyText ? emptyText : ""}</>;

  if (renderComponent && info === "organization_id") {
    return renderComponent({
      labelRender: (
        <SingleOrganization.Info
          id={data?.organization_id}
          info="organization_name"
          limitText={limitText}
        />
      ),
      goTo: () => navigationApp.goToSingleOrganization(data?.organization_id),
    });
  }
  if (info === "organization_id") {
    return (
      <SingleOrganization.Info
        id={data?.organization_id}
        info="organization_name"
        limitText={limitText}
      />
    );
  }

  const translateInfo = (valueKey: keyof UserExtend, valueRaw: any) => {
    if (!valueKey) return "";
    switch (info) {
      case "user_gender":
        return GenreTraductor(valueRaw as any);
      case "user_last_connection":
        if (valueRaw === "0001-01-01T00:00:00Z") return "Jamais connecté·e";
        return dateConverter(valueRaw as any, { withTime: true });
      case "user_created_at":
        return dateConverter(valueRaw as any);
      case "user_updated_at":
        return dateConverter(valueRaw as any);
      case "user_orga_role":
        return userRolesTraductor(valueRaw as any);
      case "user_orga_created_at":
        return dateConverter(valueRaw as any);
      case "user_orga_status":
        return translateUserStatus(valueRaw as any);
      default:
        return valueRaw;
    }
  };

  const getStatuType = (status: UserStatus): ColorType => {
    switch (status) {
      case "ACTIVATED":
        return "success";
      case "DELETED":
        return "error";
      case "DESACTIVATED":
        return "error";
      case "WAITING":
        return "warning";
      default:
        return "info";
    }
  };

  if (styled && info === "user_orga_status" && data?.user_orga_status) {
    return (
      <StatusBox
        label={translateInfo(info, data?.user_orga_status) as string}
        colorType={getStatuType(data.user_orga_status)}
        size={{ width: "100px" }}
      />
    );
  }

  const value = translateInfo(info, data?.[info] || "");

  return <>{value ? limitString(value, limitText) : emptyText}</>;
}
